import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FaTrashAlt, FaSearch, FaListAlt, FaTimesCircle, FaExclamationTriangle, FaCheckCircle, FaUserCog, FaUserShield, FaClock } from 'react-icons/fa';
import { getBajas, postBaja, getEquipoact, getAllusuarios, updatePermisoBaja, decidirBaja } from '../../../redux/actions';
import styles from './DarBaja.module.css';

const DarBaja = () => {
    const dispatch = useDispatch();

    // Cargar datos de Redux
    const rawEquipos = useSelector(state => state.equiact);
    const rawBajas = useSelector(state => state.bajas);
    const rawUsuarios = useSelector(state => state.allusuarios);
    const usuarioLogueado = useSelector(state => state.usuario);

    // Permisos del usuario actual
    const esAdmin = usuarioLogueado?.usuario?.adminis === 1;
    const userId = usuarioLogueado?.usuario?.id;

    // Stable references para listas
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const equiposAct = useMemo(() => (Array.isArray(rawEquipos) ? rawEquipos : []), [rawEquipos]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const bajas = useMemo(() => (Array.isArray(rawBajas) ? rawBajas : []), [rawBajas]);

    const allUsuariosList = useMemo(() => {
        if (rawUsuarios && Array.isArray(rawUsuarios.usuarios)) {
            return rawUsuarios.usuarios;
        }
        return Array.isArray(rawUsuarios) ? rawUsuarios : [];
    }, [rawUsuarios]);

    // Estados de navegación e inputs
    const [vista, setVista] = useState('baja'); // 'baja' | 'pendientes' | 'listado' | 'permisos'
    const [busqueda, setBusqueda] = useState('');
    const [sugerencias, setSugerencias] = useState([]);
    const [activoSeleccionado, setActivoSeleccionado] = useState(null);
    const [fechaBaja, setFechaBaja] = useState('');
    const [motivo, setMotivo] = useState('');
    const [observaciones, setObservaciones] = useState('');
    const [loading, setLoading] = useState(false);
    const [confirmar, setConfirmar] = useState(false);
    const [toast, setToast] = useState(null); 
    const [searchBajas, setSearchBajas] = useState('');
    const [searchUsuarios, setSearchUsuarios] = useState('');

    // Estado para modal de decisión de bajas (aprobar/rechazar)
    const [decisionBaja, setDecisionBaja] = useState(null); // { bajaId: 1, accion: 'aprobar'|'rechazar', cod: 'PC-001' }
    const [notasDecision, setNotasDecision] = useState('');

    // Cargar equipos activos al montar
    useEffect(() => {
        dispatch(getEquipoact());
    }, [dispatch]);

    // Cargar bajas según pestaña
    useEffect(() => {
        if (vista === 'listado' || vista === 'pendientes') {
            dispatch(getBajas());
        }
    }, [vista, dispatch]);

    // Cargar lista de usuarios al entrar a la configuración de permisos
    useEffect(() => {
        if (vista === 'permisos') {
            dispatch(getAllusuarios());
        }
    }, [vista, dispatch]);

    // Filtrar sugerencias de activos
    useEffect(() => {
        if (!busqueda.trim()) { setSugerencias([]); return; }
        const lower = busqueda.toLowerCase();
        const filtrados = (Array.isArray(equiposAct) ? equiposAct : []).filter(item => {
            const eq = item.equipo || item;
            return (
                (eq.cod && eq.cod.toLowerCase().includes(lower)) ||
                (eq.modelo && eq.modelo.toLowerCase().includes(lower)) ||
                (eq.serial && eq.serial.toLowerCase().includes(lower))
            );
        }).slice(0, 8);
        setSugerencias(filtrados);
    }, [busqueda, equiposAct]);

    const mostrarToast = (tipo, msg) => {
        setToast({ tipo, msg });
        setTimeout(() => setToast(null), 4000);
    };

    const seleccionarActivo = (item) => {
        setActivoSeleccionado(item);
        setBusqueda(item.equipo?.cod || item.cod || '');
        setSugerencias([]);
    };

    const limpiarFormulario = () => {
        setActivoSeleccionado(null);
        setBusqueda('');
        setFechaBaja('');
        setMotivo('');
        setObservaciones('');
        setConfirmar(false);
    };

    const handleDarDeBaja = async () => {
        if (!activoSeleccionado || !fechaBaja || !motivo.trim() || !userId) return;
        setLoading(true);
        try {
            const equipoId = activoSeleccionado.equipo?.id || activoSeleccionado.id;
            const res = await dispatch(postBaja({ 
                equipoid: equipoId, 
                fecha: fechaBaja, 
                motivo, 
                observaciones,
                usuarioId: userId
            }));
            
            mostrarToast('exito', res.message || 'Baja registrada con éxito.');
            limpiarFormulario();
            dispatch(getEquipoact());
        } catch (e) {
            mostrarToast('error', e?.response?.data?.message || 'Error al procesar la baja.');
        } finally {
            setLoading(false);
            setConfirmar(false);
        }
    };

    const handleTogglePermiso = async (usuarioId, actualPermiso) => {
        try {
            const nuevoPermiso = actualPermiso === 1 ? 0 : 1;
            await dispatch(updatePermisoBaja(usuarioId, nuevoPermiso));
            mostrarToast('exito', 'Permisos de usuario actualizados.');
        } catch (error) {
            mostrarToast('error', 'Error al modificar permisos.');
        }
    };

    const handleDecidirBaja = async () => {
        if (!decisionBaja || !userId) return;
        setLoading(true);
        try {
            await dispatch(decidirBaja(decisionBaja.bajaId, {
                accion: decisionBaja.accion,
                adminId: userId,
                observaciones: notasDecision
            }));
            
            mostrarToast('exito', `Solicitud de baja ${decisionBaja.accion === 'aprobar' ? 'aprobada' : 'rechazada'} con éxito.`);
            setDecisionBaja(null);
            setNotasDecision('');
            // Refrescar datos
            dispatch(getBajas());
            dispatch(getEquipoact());
        } catch (error) {
            mostrarToast('error', error?.response?.data?.message || 'Error al registrar la decisión.');
        } finally {
            setLoading(false);
        }
    };

    const formularioValido = activoSeleccionado && fechaBaja && motivo.trim().length >= 5;

    // Filtros de listados
    const bajasPendientes = bajas.filter(item => item.baja?.estado === 'pendiente');
    const bajasHistorial = bajas.filter(item => item.baja?.estado !== 'pendiente');

    const bajasFiltradas = bajasHistorial.filter(item => {
        const lower = searchBajas.toLowerCase();
        if (!lower) return true;
        const eq = item.equipo || {};
        return (
            (eq.cod && eq.cod.toLowerCase().includes(lower)) ||
            (eq.modelo && eq.modelo.toLowerCase().includes(lower)) ||
            (eq.serial && eq.serial.toLowerCase().includes(lower)) ||
            (item.baja?.motivo && item.baja.motivo.toLowerCase().includes(lower))
        );
    });

    const usuariosFiltrados = allUsuariosList.filter(user => {
        const lower = searchUsuarios.toLowerCase();
        if (!lower) return true;
        return (
            (user.nombre && user.nombre.toLowerCase().includes(lower)) ||
            (user.cedula && user.cedula.toLowerCase().includes(lower)) ||
            (user.cargo && user.cargo.toLowerCase().includes(lower))
        );
    });

    return (
        <div className={styles.container}>
            {/* Toast notification */}
            {toast && (
                <div className={`${styles.toast} ${styles[`toast_${toast.tipo}`]}`}>
                    {toast.tipo === 'exito' ? <FaCheckCircle /> : <FaExclamationTriangle />}
                    <span>{toast.msg}</span>
                </div>
            )}

            {/* Header */}
            <div className={styles.header}>
                <div className={styles.headerIcon}>
                    <FaTrashAlt />
                </div>
                <div>
                    <h1 className={styles.headerTitle}>Dar de Baja Activos</h1>
                    <p className={styles.headerSubtitle}>Gestiona las bajas de activos del inventario institucional</p>
                </div>
            </div>

            {/* Tabs */}
            <div className={styles.tabBar}>
                <button
                    className={`${styles.tab} ${vista === 'baja' ? styles.tabActive : ''}`}
                    onClick={() => setVista('baja')}
                >
                    <FaTrashAlt style={{ marginRight: 8 }} />
                    {esAdmin ? 'Registrar Baja' : 'Proponer Baja'}
                </button>
                {esAdmin && (
                    <button
                        className={`${styles.tab} ${vista === 'pendientes' ? styles.tabActive : ''}`}
                        onClick={() => setVista('pendientes')}
                    >
                        <FaClock style={{ marginRight: 8, color: vista === 'pendientes' ? '#fff' : '#d97706' }} />
                        Pendientes de Aprobación ({bajasPendientes.length})
                    </button>
                )}
                <button
                    className={`${styles.tab} ${vista === 'listado' ? styles.tabActive : ''}`}
                    onClick={() => setVista('listado')}
                >
                    <FaListAlt style={{ marginRight: 8 }} />
                    Historial de Bajas
                </button>
                {esAdmin && (
                    <button
                        className={`${styles.tab} ${vista === 'permisos' ? styles.tabActive : ''}`}
                        onClick={() => setVista('permisos')}
                    >
                        <FaUserCog style={{ marginRight: 8 }} />
                        Permisos de Usuarios
                    </button>
                )}
            </div>

            {/* ========== VISTA: REGISTRAR/PROPONER BAJA ========== */}
            {vista === 'baja' && (
                <div className={styles.content}>
                    {!esAdmin && (
                        <div className={styles.infoAlert}>
                            <FaClock size={20} />
                            <span>Nota: Al no ser administrador, tu propuesta de baja quedará en revisión pendiente de autorización.</span>
                        </div>
                    )}
                    <div className={styles.content}>
                        <div className={styles.card}>
                            <h2 className={styles.cardTitle}>
                                <span className={styles.stepBadge}>1</span>
                                Buscar Activo
                            </h2>
                            <div className={styles.searchBox}>
                                <div className={styles.searchInputWrap}>
                                    <FaSearch className={styles.searchIcon} />
                                    <input
                                        type="text"
                                        className={styles.searchInput}
                                        placeholder="Buscar por código, modelo o serial..."
                                        value={busqueda}
                                        onChange={e => { setBusqueda(e.target.value); setActivoSeleccionado(null); }}
                                        autoComplete="off"
                                    />
                                    {busqueda && (
                                        <button className={styles.clearBtn} onClick={limpiarFormulario}>
                                            <FaTimesCircle />
                                        </button>
                                    )}
                                </div>
                                {/* Dropdown de sugerencias */}
                                {sugerencias.length > 0 && (
                                    <ul className={styles.suggList}>
                                        {sugerencias.map((item, i) => {
                                            const eq = item.equipo || item;
                                            return (
                                                <li key={eq.id || i} className={styles.suggItem} onClick={() => seleccionarActivo(item)}>
                                                    <span className={styles.suggCod}>{eq.cod}</span>
                                                    <span className={styles.suggModel}>{eq.modelo}</span>
                                                    <span className={styles.suggSerial}>S/N: {eq.serial}</span>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                )}
                            </div>

                            {/* Info del activo seleccionado */}
                            {activoSeleccionado && (
                                <div className={styles.activoCard}>
                                    <div className={styles.activoBadge}>Activo Seleccionado</div>
                                    <div className={styles.activoGrid}>
                                        <div className={styles.activoField}>
                                            <span className={styles.fieldLabel}>Código</span>
                                            <span className={styles.fieldValue}>{activoSeleccionado.equipo?.cod}</span>
                                        </div>
                                        <div className={styles.activoField}>
                                            <span className={styles.fieldLabel}>Modelo</span>
                                            <span className={styles.fieldValue}>{activoSeleccionado.equipo?.modelo || '-'}</span>
                                        </div>
                                        <div className={styles.activoField}>
                                            <span className={styles.fieldLabel}>Serial</span>
                                            <span className={styles.fieldValue}>{activoSeleccionado.equipo?.serial || '-'}</span>
                                        </div>
                                        <div className={styles.activoField}>
                                            <span className={styles.fieldLabel}>Tipo</span>
                                            <span className={styles.fieldValue}>{activoSeleccionado.tipodeequipo?.descripcion || '-'}</span>
                                        </div>
                                        <div className={styles.activoField}>
                                            <span className={styles.fieldLabel}>Marca</span>
                                            <span className={styles.fieldValue}>{activoSeleccionado.marca?.marca || '-'}</span>
                                        </div>
                                        <div className={styles.activoField}>
                                            <span className={styles.fieldLabel}>Sede</span>
                                            <span className={styles.fieldValue}>{activoSeleccionado.equipo?.sede || '-'}</span>
                                        </div>
                                        <div className={styles.activoField}>
                                            <span className={styles.fieldLabel}>Dependencia</span>
                                            <span className={styles.fieldValue}>{activoSeleccionado.equipo?.dependencia || '-'}</span>
                                        </div>
                                        <div className={styles.activoField}>
                                            <span className={styles.fieldLabel}>Valor</span>
                                            <span className={`${styles.fieldValue} ${styles.valorField}`}>
                                                ${activoSeleccionado.equipo?.valor?.toLocaleString('es-CO') || '0'}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* PASO 2: Datos de la baja */}
                        <div className={`${styles.card} ${!activoSeleccionado ? styles.cardDisabled : ''}`}>
                            <h2 className={styles.cardTitle}>
                                <span className={styles.stepBadge}>2</span>
                                Datos de la Baja
                            </h2>

                            <div className={styles.formGrid}>
                                <div className={styles.formGroup}>
                                    <label className={styles.formLabel}>Fecha de Baja <span className={styles.required}>*</span></label>
                                    <input
                                        type="date"
                                        className={styles.formInput}
                                        value={fechaBaja}
                                        onChange={e => setFechaBaja(e.target.value)}
                                        max={new Date().toISOString().split('T')[0]}
                                        disabled={!activoSeleccionado}
                                    />
                                </div>

                                <div className={styles.formGroup} style={{ gridColumn: '1 / -1' }}>
                                    <label className={styles.formLabel}>Motivo de Baja <span className={styles.required}>*</span></label>
                                    <select
                                        className={styles.formInput}
                                        value={motivo.startsWith('Otro') ? 'Otro' : motivo}
                                        onChange={e => setMotivo(e.target.value)}
                                        disabled={!activoSeleccionado}
                                    >
                                        <option value="">— Seleccione un motivo —</option>
                                        <option value="Obsolescencia tecnológica">Obsolescencia tecnológica</option>
                                        <option value="Daño irreparable">Daño irreparable</option>
                                        <option value="Robo o hurto">Robo o hurto</option>
                                        <option value="Donación o transferencia">Donación o transferencia</option>
                                        <option value="Extravío">Extravío</option>
                                        <option value="Fin de vida útil">Fin de vida útil</option>
                                        <option value="Chatarrización">Chatarrización</option>
                                        <option value="Otro">Otro (especificar en observaciones)</option>
                                    </select>
                                </div>

                                <div className={styles.formGroup} style={{ gridColumn: '1 / -1' }}>
                                    <label className={styles.formLabel}>Observaciones adicionales</label>
                                    <textarea
                                        className={styles.formTextarea}
                                        placeholder="Describa detalles adicionales sobre la baja..."
                                        value={observaciones}
                                        onChange={e => setObservaciones(e.target.value)}
                                        rows={3}
                                        disabled={!activoSeleccionado}
                                    />
                                </div>
                            </div>

                            <div className={styles.actionRow}>
                                <button
                                    className={styles.cancelBtn}
                                    onClick={limpiarFormulario}
                                    disabled={!activoSeleccionado}
                                >
                                    Limpiar
                                </button>
                                <button
                                    className={styles.bajaBtn}
                                    onClick={() => setConfirmar(true)}
                                    disabled={!formularioValido || loading}
                                >
                                    <FaTrashAlt style={{ marginRight: 8 }} />
                                    {esAdmin ? 'Registrar Baja' : 'Proponer Baja'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ========== VISTA: SOLICITUDES PENDIENTES (Solo Admin) ========== */}
            {vista === 'pendientes' && esAdmin && (
                <div className={styles.content}>
                    <div className={styles.card}>
                        <h2 className={styles.cardTitle}>
                            <FaClock style={{ marginRight: 10, color: '#d97706' }} />
                            Solicitudes de Baja Pendientes ({bajasPendientes.length})
                        </h2>

                        {bajasPendientes.length === 0 ? (
                            <div className={styles.emptyState}>
                                <FaClock size={40} color="#cbd5e1" />
                                <p>No hay solicitudes de baja pendientes de aprobación.</p>
                            </div>
                        ) : (
                            <div className={styles.tableWrap}>
                                <table className={styles.table}>
                                    <thead>
                                        <tr>
                                            <th>Código</th>
                                            <th>Modelo</th>
                                            <th>Serial</th>
                                            <th>Solicitante</th>
                                            <th>Fecha Propuesta</th>
                                            <th>Motivo</th>
                                            <th>Observaciones</th>
                                            <th style={{ textAlign: 'center' }}>Acciones</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {bajasPendientes.map((item, i) => (
                                            <tr key={item.baja?.id || i} className={i % 2 === 0 ? styles.rowEven : styles.rowOdd}>
                                                <td className={styles.codCell}>{item.equipo?.cod}</td>
                                                <td>{item.equipo?.modelo}</td>
                                                <td>{item.equipo?.serial}</td>
                                                <td style={{ fontWeight: 600 }}>{item.solicitante?.nombre || 'Usuario'}</td>
                                                <td className={styles.fechaCell}>
                                                    {item.baja?.fecha ? new Date(item.baja.fecha).toLocaleDateString('es-ES') : '-'}
                                                </td>
                                                <td>
                                                    <span className={styles.statusBadge_pendiente}>{item.baja?.motivo}</span>
                                                </td>
                                                <td className={styles.obsCell}>{item.baja?.observaciones || '-'}</td>
                                                <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                                                    <button 
                                                        className={styles.approveBtn}
                                                        onClick={() => setDecisionBaja({ bajaId: item.baja.id, accion: 'aprobar', cod: item.equipo?.cod })}
                                                    >
                                                        Aprobar
                                                    </button>
                                                    <button 
                                                        className={styles.rejectBtn}
                                                        onClick={() => setDecisionBaja({ bajaId: item.baja.id, accion: 'rechazar', cod: item.equipo?.cod })}
                                                    >
                                                        Rechazar
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* ========== VISTA: LISTADO HISTORIAL DE BAJAS ========== */}
            {vista === 'listado' && (
                <div className={styles.content}>
                    <div className={styles.card}>
                        <div className={styles.listHeader}>
                            <h2 className={styles.cardTitle} style={{ margin: 0 }}>
                                <FaListAlt style={{ marginRight: 10, color: '#dc2626' }} />
                                Historial de Bajas ({bajasFiltradas.length})
                            </h2>
                            <div className={styles.searchInputWrap} style={{ maxWidth: 280 }}>
                                <FaSearch className={styles.searchIcon} />
                                <input
                                    type="text"
                                    className={styles.searchInput}
                                    placeholder="Buscar en historial..."
                                    value={searchBajas}
                                    onChange={e => setSearchBajas(e.target.value)}
                                />
                            </div>
                        </div>

                        {bajasFiltradas.length === 0 ? (
                            <div className={styles.emptyState}>
                                <FaListAlt size={40} color="#cbd5e1" />
                                <p>No hay registros de baja procesados.</p>
                            </div>
                        ) : (
                            <div className={styles.tableWrap}>
                                <table className={styles.table}>
                                    <thead>
                                        <tr>
                                            <th>Código</th>
                                            <th>Modelo</th>
                                            <th>Serial</th>
                                            <th>Solicitado por</th>
                                            <th>Aprobado por</th>
                                            <th>Fecha</th>
                                            <th>Motivo</th>
                                            <th>Estado</th>
                                            <th>Detalles</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {bajasFiltradas.map((item, i) => (
                                            <tr key={item.baja?.id || i} className={i % 2 === 0 ? styles.rowEven : styles.rowOdd}>
                                                <td className={styles.codCell}>{item.equipo?.cod}</td>
                                                <td>{item.equipo?.modelo}</td>
                                                <td>{item.equipo?.serial}</td>
                                                <td>{item.solicitante?.nombre || '-'}</td>
                                                <td>{item.aprobador?.nombre || '-'}</td>
                                                <td className={styles.fechaCell}>
                                                    {item.baja?.fecha ? new Date(item.baja.fecha).toLocaleDateString('es-ES') : '-'}
                                                </td>
                                                <td>
                                                    <span className={styles.motivoBadge}>{item.baja?.motivo}</span>
                                                </td>
                                                <td>
                                                    <span className={styles[`statusBadge_${item.baja?.estado}`]}>
                                                        {item.baja?.estado?.toUpperCase()}
                                                    </span>
                                                </td>
                                                <td className={styles.obsCell}>{item.baja?.observaciones || '-'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* ========== VISTA: PERMISOS DE USUARIOS (Solo Admin) ========== */}
            {vista === 'permisos' && esAdmin && (
                <div className={styles.content}>
                    <div className={styles.card}>
                        <div className={styles.listHeader}>
                            <h2 className={styles.cardTitle} style={{ margin: 0 }}>
                                <FaUserShield style={{ marginRight: 10, color: '#2563eb' }} />
                                Configurar Acceso a Bajas de Activos
                            </h2>
                            <div className={styles.searchInputWrap} style={{ maxWidth: 280 }}>
                                <FaSearch className={styles.searchIcon} />
                                <input
                                    type="text"
                                    className={styles.searchInput}
                                    placeholder="Buscar usuario..."
                                    value={searchUsuarios}
                                    onChange={e => setSearchUsuarios(e.target.value)}
                                />
                            </div>
                        </div>

                        {usuariosFiltrados.length === 0 ? (
                            <div className={styles.emptyState}>
                                <FaUserCog size={40} color="#cbd5e1" />
                                <p>No se encontraron usuarios.</p>
                            </div>
                        ) : (
                            <div className={styles.tableWrap}>
                                <table className={styles.table}>
                                    <thead>
                                        <tr>
                                            <th>Nombre Completo</th>
                                            <th>Cédula</th>
                                            <th>Cargo</th>
                                            <th>Área</th>
                                            <th>Perfil</th>
                                            <th style={{ textAlign: 'center' }}>¿Puede Proponer Bajas?</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {usuariosFiltrados.map((user, i) => (
                                            <tr key={user.id || i} className={i % 2 === 0 ? styles.rowEven : styles.rowOdd}>
                                                <td style={{ fontWeight: 600 }}>{user.nombre}</td>
                                                <td>{user.cedula}</td>
                                                <td>{user.cargo || '-'}</td>
                                                <td>{user.area || '-'}</td>
                                                <td>
                                                    {user.administracion === 1 ? (
                                                        <span className={styles.adminBadge}>Administración</span>
                                                    ) : (
                                                        <span style={{ fontSize: '0.8rem', color: '#64748b' }}>Usuario estándar</span>
                                                    )}
                                                </td>
                                                <td style={{ textAlign: 'center' }}>
                                                    {user.administracion === 1 ? (
                                                        <span style={{ fontSize: '0.8rem', fontWeight: 700, color: '#16a34a' }}>Permitido (Admin)</span>
                                                    ) : (
                                                        <label className={styles.switch}>
                                                            <input
                                                                type="checkbox"
                                                                checked={user.permisoBaja === 1}
                                                                onChange={() => handleTogglePermiso(user.id, user.permisoBaja)}
                                                            />
                                                            <span className={styles.slider}></span>
                                                        </label>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* ========== MODAL DE CONFIRMACIÓN (CREAR BAJA PENDIENTE / DIRECTA) ========== */}
            {confirmar && (
                <div className={styles.overlay}>
                    <div className={styles.modal}>
                        <div className={styles.modalIcon}>
                            <FaExclamationTriangle />
                        </div>
                        <h3 className={styles.modalTitle}>¿Confirmar {esAdmin ? 'Baja' : 'Solicitud'}?</h3>
                        <p className={styles.modalDesc}>
                            {esAdmin 
                                ? `Estás a punto de dar de baja definitiva el activo ${activoSeleccionado?.equipo?.cod}.`
                                : `Se registrará una propuesta de baja para el activo ${activoSeleccionado?.equipo?.cod} que quedará pendiente de aprobación.`
                            }
                        </p>
                        <div className={styles.modalInfo}>
                            <span><strong>Fecha:</strong> {fechaBaja}</span>
                            <span><strong>Motivo:</strong> {motivo}</span>
                        </div>
                        <div className={styles.modalActions}>
                            <button className={styles.cancelBtn} onClick={() => setConfirmar(false)} disabled={loading}>
                                Cancelar
                            </button>
                            <button className={styles.bajaBtn} onClick={handleDarDeBaja} disabled={loading}>
                                {loading ? 'Procesando...' : (esAdmin ? 'Sí, dar de baja' : 'Sí, proponer')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ========== MODAL DE DECISIÓN DE BAJAS PENDIENTES (APROBAR/RECHAZAR) ========== */}
            {decisionBaja && (
                <div className={styles.overlay}>
                    <div className={styles.modal}>
                        <div className={styles.modalIcon} style={{ background: decisionBaja.accion === 'aprobar' ? '#ecfdf5' : '#fef2f2', borderColor: decisionBaja.accion === 'aprobar' ? '#a7f3d0' : '#fecaca', color: decisionBaja.accion === 'aprobar' ? '#10b981' : '#ef4444' }}>
                            <FaExclamationTriangle />
                        </div>
                        <h3 className={styles.modalTitle}>
                            ¿{decisionBaja.accion === 'aprobar' ? 'Aprobar' : 'Rechazar'} Solicitud?
                        </h3>
                        <p className={styles.modalDesc}>
                            Activo: <strong>{decisionBaja.cod}</strong>. 
                            {decisionBaja.accion === 'aprobar' 
                                ? ' Al aprobar, el activo será retirado de forma permanente del inventario activo.'
                                : ' Al rechazar, la propuesta quedará registrada como denegada y el activo seguirá estando disponible.'
                            }
                        </p>

                        <div style={{ textAlign: 'left', marginBottom: 20 }}>
                            <label className={styles.formLabel}>Comentario / Justificación de la decisión</label>
                            <textarea
                                className={styles.formTextarea}
                                style={{ width: '100%', boxSizing: 'border-box', marginTop: 6 }}
                                placeholder="Indique la justificación para esta decisión..."
                                value={notasDecision}
                                onChange={e => setNotasDecision(e.target.value)}
                                rows={3}
                            />
                        </div>

                        <div className={styles.modalActions}>
                            <button className={styles.cancelBtn} onClick={() => { setDecisionBaja(null); setNotasDecision(''); }} disabled={loading}>
                                Cancelar
                            </button>
                            <button 
                                className={styles.bajaBtn} 
                                style={{ background: decisionBaja.accion === 'aprobar' ? 'linear-gradient(135deg, #10b981, #059669)' : 'linear-gradient(135deg, #dc2626, #ef4444)' }}
                                onClick={handleDecidirBaja} 
                                disabled={loading}
                            >
                                {loading ? 'Guardando...' : (decisionBaja.accion === 'aprobar' ? 'Aprobar de Baja' : 'Rechazar Propuesta')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DarBaja;
